import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

interface ProfileGuardProps {
  children: React.ReactNode;
  redirectTo?: string;
}

/**
 * ProfileGuard component to ensure users have created both profiles
 *
 * Checks if the authenticated user has both:
 * 1. Basic user profile (hasProfile)
 * 2. Blockchain NFT profile (hasBlockchainProfile)
 *
 * If either is missing, redirects to profile creation page.
 * This should be used to wrap routes that require a complete setup.
 *
 * @param children - Child components to render if user has both profiles
 * @param redirectTo - Where to redirect if no profile (default: '/create-profile')
 *
 * @example
 * <ProfileGuard>
 *   <Dashboard />
 * </ProfileGuard>
 */
export const ProfileGuard: React.FC<ProfileGuardProps> = ({
  children,
  redirectTo = '/create-profile',
}) => {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  // Show loading state while checking
  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  // If user doesn't have a basic profile, redirect to profile creation
  if (user && user.hasProfile === false) {
    // Save the location they were trying to access
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // If user has basic profile but no blockchain profile, redirect to retry
  if (user && user.hasProfile === true && user.hasBlockchainProfile === false) {
    console.warn('⚠️  User has profile but missing blockchain profile, redirecting to create profile for retry');
    return <Navigate to={redirectTo} state={{ from: location, retryBlockchain: true }} replace />;
  }

  // User has both profiles, render children
  return <>{children}</>;
};

export default ProfileGuard;
